import {
  HighLevelSolidityType,
  safeValidateAddress,
} from '@mls-wallet/shared-types';
import type { AbiParameter } from 'viem';
import type { TransactionBuilderParameterState } from './types';
import { getArrayChildType, getValueTypeFromSolidityType } from './utils';

function parseAggregateState(
  value: string
): TransactionBuilderParameterState[] {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? (parsed as TransactionBuilderParameterState[])
      : [];
  } catch {
    return [];
  }
}

/**
 * Collect valid address leaves from editable parameter state without requiring
 * every sibling parameter to be valid. Array and tuple state is stored as
 * serialized child field states, so this follows the ABI alongside that tree.
 */
export function collectAddressParameterValues(
  inputs: AbiParameter[],
  parameterStates: TransactionBuilderParameterState[]
): string[] {
  const addresses = new Set<string>();

  const visit = (
    input: AbiParameter,
    state: TransactionBuilderParameterState | undefined
  ): void => {
    if (!state) return;

    const valueType = getValueTypeFromSolidityType(input.type);

    if (valueType === HighLevelSolidityType.ADDRESS) {
      const candidate =
        typeof state.parsedValue === 'string' ? state.parsedValue : state.value;
      if (typeof candidate !== 'string') return;

      const validation = safeValidateAddress(candidate);
      if (validation.isValid) {
        addresses.add(validation.checksumAddress);
      }
      return;
    }

    const childStates = parseAggregateState(state.value);

    if (valueType === HighLevelSolidityType.ARRAY) {
      const childType = getArrayChildType(input.type);
      if (!childType) return;

      const childInput: AbiParameter = {
        ...input,
        type: childType,
        ...('components' in input && input.components
          ? { components: input.components }
          : {}),
      };
      childStates.forEach((childState) => visit(childInput, childState));
      return;
    }

    if (
      valueType === HighLevelSolidityType.STRUCT_OR_TUPLE &&
      'components' in input &&
      input.components
    ) {
      input.components.forEach((component, index) =>
        visit(component, childStates[index])
      );
    }
  };

  inputs.forEach((input, index) => visit(input, parameterStates[index]));
  return [...addresses];
}
